// React and Next.js core imports
import React from "react";
import * as Sentry from "@sentry/nextjs";

// Types
interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

// Global Error Boundary Component
const GlobalErrorBoundary: React.FC<Props> = ({ children }) => {
  const [state, setState] = React.useState<State>({
    hasError: false,
    error: null,
  });

  React.useEffect(() => {
    // Initialize error tracking
    const handleError = (error: ErrorEvent) => {
      setState({ hasError: true, error: error.error });
      Sentry.captureException(error.error);
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      setState({ hasError: true, error: event.reason });
      Sentry.captureException(event.reason);
    };

    window.addEventListener("error", handleError);
    window.addEventListener("unhandledrejection", handleUnhandledRejection);

    return () => {
      window.removeEventListener("error", handleError);
      window.removeEventListener(
        "unhandledrejection",
        handleUnhandledRejection
      );
    };
  }, []);

  if (state.hasError) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center p-4 bg-gray-50">
        <div className="max-w-md w-full space-y-8 p-8 bg-white rounded-lg shadow-lg">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-red-600 mb-4">
              Oops! Something went wrong
            </h2>
            <p className="text-gray-600 mb-6">
              We apologize for the inconvenience. Our team has been notified and
              is working to fix the issue.
            </p>
            <div className="space-y-4">
              <button
                onClick={() => {
                  setState({ hasError: false, error: null });
                  window.location.reload();
                }}
                className="w-full px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
              >
                Try again
              </button>
              <button
                onClick={() => (window.location.href = "/")}
                className="w-full px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors"
              >
                Go to Homepage
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default GlobalErrorBoundary;
